/**
 * Library Matcher Service
 * Efficiently matches search results against the existing game library
 * Uses fuzzy string matching without requiring additional LLM calls
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rgm.games/api';

// Cache for library games
let libraryCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const TITLE_CANDIDATE_THRESHOLD = 0.65;
const MATCH_THRESHOLD = 0.72;
const PLATFORM_MATCH_BONUS = 0.05;

// Platform ID to name mapping (from rgm.games)
const PLATFORM_MAP = {
  1: ['atari', 'atari 2600', 'a2600'],
  2: ['nes', 'fc', 'famicom', 'nintendo'],
  3: ['snes', 'sfc', 'super famicom', 'super nintendo'],
  4: ['genesis', 'mega drive', 'md', 'sega genesis'],
  5: ['ps', 'ps1', 'psx', 'playstation'],
  6: ['gba', 'game boy advance', 'gameboy advance'],
  7: ['dos', 'ms-dos', 'msdos'],
  8: ['arcade'],
  9: ['psp', 'playstation portable'],
  10: ['nds', 'ds', 'nintendo ds'],
  11: ['flash', 'swf'],
  12: ['windows', 'win95', 'win98', 'pc'],
  13: ['apple ii', 'apple 2'],
  14: ['virtual boy'],
  15: ['infocom', 'text adventure'],
};

/**
 * Normalize a string for comparison
 * Removes special characters, converts to lowercase, handles common variations
 */
function normalizeString(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^\w\s\u4e00-\u9fff]/g, '') // Keep alphanumeric, spaces, and Chinese chars
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Calculate string similarity (0-1) based on Levenshtein distance
 */
function stringSimilarity(a, b) {
  const normA = normalizeString(a);
  const normB = normalizeString(b);
  
  if (normA === normB) return 1;
  if (!normA || !normB) return 0;
  
  const maxLen = Math.max(normA.length, normB.length);
  if (maxLen === 0) return 1;
  
  const distance = levenshteinDistance(normA, normB);
  return 1 - distance / maxLen;
}

/**
 * Check if two strings are similar enough to be considered a match
 * Uses multiple strategies for better accuracy
 */
function titleMatch(searchTitle, libraryTitle) {
  const normSearch = normalizeString(searchTitle);
  const normLibrary = normalizeString(libraryTitle);

  if (!normSearch || !normLibrary) return 0;
  
  // Exact match
  if (normSearch === normLibrary) return 1;
  
  // One contains the other (for subtitles, etc.)
  if (normSearch.includes(normLibrary) || normLibrary.includes(normSearch)) {
    const shorter = Math.min(normSearch.length, normLibrary.length);
    const longer = Math.max(normSearch.length, normLibrary.length);
    const shorterTitle = normSearch.length <= normLibrary.length ? normSearch : normLibrary;
    const isMeaningfulBase = /[\u4e00-\u9fff]{3,}/.test(shorterTitle)
      || shorterTitle.replace(/\s/g, '').length >= 4;
    const partialScore = shorter / longer * 0.95;

    // A meaningful contained title is usually the same game with a version,
    // edition, region, or subtitle suffix (for example 我的世界 vs 我的世界1.12.2).
    return isMeaningfulBase ? Math.max(partialScore, 0.85) : partialScore;
  }
  
  // Levenshtein similarity
  return stringSimilarity(searchTitle, libraryTitle);
}

/**
 * Check if platforms match
 */
function platformMatch(searchPlatform, libraryPlatformId) {
  const normSearch = normalizeString(searchPlatform);
  const platformNames = PLATFORM_MAP[libraryPlatformId];
  
  if (!platformNames) return false;
  
  for (const name of platformNames) {
    if (normSearch.includes(name) || name.includes(normSearch)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Fetch library games from API
 */
async function fetchLibraryGames() {
  try {
    const response = await fetch(`${API_BASE_URL}/games`);
    if (!response.ok) {
      console.error('Failed to fetch library games:', response.status);
      return [];
    }
    
    const games = await response.json();
    return games.map((g) => ({
      id: g.id,
      titleeng: g.titleeng || '',
      titlechn: g.titlechn || '',
      platform: g.platform,
      year: g.year,
      dev: g.dev || '',
    }));
  } catch (error) {
    console.error('Error fetching library games:', error);
    return [];
  }
}

/**
 * Get library games (with caching)
 */
export async function getLibraryGames() {
  const now = Date.now();
  
  if (libraryCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return libraryCache;
  }
  
  libraryCache = await fetchLibraryGames();
  cacheTimestamp = now;
  return libraryCache;
}

/**
 * Find the best match for a game in the library
 * Returns match info including confidence score
 */
export async function findLibraryMatch(titleEn, titleCn, platform, releaseYear) {
  const library = await getLibraryGames();
  
  if (library.length === 0) {
    return { isInLibrary: false, confidence: 0 };
  }
  
  let bestMatch = null;
  
  for (const game of library) {
    // Calculate title similarity (check both English and Chinese)
    const engSimilarity = Math.max(
      titleMatch(titleEn, game.titleeng),
      titleMatch(titleCn, game.titleeng)
    );
    const chnSimilarity = Math.max(
      titleMatch(titleEn, game.titlechn),
      titleMatch(titleCn, game.titlechn)
    );
    
    const titleScore = Math.max(engSimilarity, chnSimilarity);
    
    // Keep close title variants as candidates. Platform is intentionally not a
    // requirement because ports can have the same basic title.
    if (titleScore < TITLE_CANDIDATE_THRESHOLD) continue;
    
    // Check platform match
    const platformMatches = platformMatch(platform, game.platform);
    
    // Calculate year similarity (optional, adds confidence)
    let yearBonus = 0;
    if (releaseYear && game.year) {
      const searchYear = parseInt(releaseYear.substring(0, 4));
      if (!isNaN(searchYear)) {
        const yearDiff = Math.abs(searchYear - game.year);
        if (yearDiff === 0) yearBonus = 0.1;
        else if (yearDiff <= 1) yearBonus = 0.05;
      }
    }
    
    const platformBonus = platformMatches ? PLATFORM_MATCH_BONUS : 0;
    const totalScore = Math.min(titleScore + platformBonus + yearBonus, 1);
    
    if (!bestMatch || totalScore > bestMatch.score) {
      bestMatch = { game, score: totalScore };
    }
  }
  
  if (bestMatch && bestMatch.score >= MATCH_THRESHOLD) {
    return {
      isInLibrary: true,
      libraryGameId: bestMatch.game.id,
      confidence: bestMatch.score,
    };
  }
  
  return { isInLibrary: false, confidence: bestMatch?.score || 0 };
}

/**
 * Batch check multiple games against the library
 * More efficient than checking one by one
 */
export async function batchFindLibraryMatches(games) {
  // Pre-fetch library once
  await getLibraryGames();
  
  // Check all games
  return Promise.all(
    games.map(game => 
      findLibraryMatch(game.titleEn, game.titleCn, game.platform, game.releaseYear)
    )
  );
}

/**
 * Clear the library cache (useful for testing or when library is updated)
 */
export function clearLibraryCache() {
  libraryCache = null;
  cacheTimestamp = 0;
}
